import { useState } from 'react';
import type { ReactElement, ReactNode } from 'react';
import type { WidgetProps } from '@rjsf/utils';
import { Box, Button, CircularProgress, Menu, MenuItem } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { useSearchParams } from 'react-router-dom';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { CSS } from '@dnd-kit/utilities';

import type { Asset } from '../../../Pages/Section/hooks/useGetAssets';
import { useUploadAsset } from '../../../Pages/Section/hooks/useUploadAsset';
import { AssetPickerModal } from '../../AssetPickerModal';
import { FileUploadModal } from '../../FileUploadModal';
import { ImageCropModal } from '../../ImageCropModal';
import type { LinkFields } from './ImageLinkRow';
import { ImageLinkRow } from './ImageLinkRow';
import type { ImageLinkItem } from './ImageLinkRow';

interface ImageLinkManagerOptions {
  imageSources?: string[];
  sources?: string[];
  sortable?: boolean;
  addButtonLabel?: string;
  aspectRatio?: string;
}

const SOURCE_LABELS: Record<string, string> = {
  disk: 'From disk',
  assets: 'From assets',
};

function buildDefaultFileName(originalName: string, aspectRatio?: string): string {
  const lastDot = originalName.lastIndexOf('.');
  const base = lastDot >= 0 ? originalName.slice(0, lastDot) : originalName;
  const ext = lastDot >= 0 ? originalName.slice(lastDot) : '';
  const suffix = aspectRatio ? `-${aspectRatio.replace(':', 'x')}` : '';
  return `${base}${suffix}${ext}`;
}

function getLinkHrefRequired(schema: WidgetProps['schema']): boolean {
  const rawItems = schema.items;
  if (!rawItems || typeof rawItems === 'boolean') return false;
  const itemSchema = Array.isArray(rawItems) ? rawItems[0] : rawItems;
  if (!itemSchema || typeof itemSchema === 'boolean') return false;

  return (itemSchema as { required?: string[] }).required?.includes('linkHref') ?? false;
}

interface SortableRowProps {
  id: string;
  item: ImageLinkItem;
  onLinkChange: (fields: LinkFields) => void;
  onLinkClear: () => void;
  onDelete: () => void;
  linkHrefRequired: boolean;
  error?: string;
}

function SortableImageLinkRow({
  id,
  item,
  onLinkChange,
  onLinkClear,
  onDelete,
  linkHrefRequired,
  error,
}: SortableRowProps): ReactElement {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });

  return (
    <ImageLinkRow
      ref={setNodeRef}
      item={item}
      onLinkChange={onLinkChange}
      onLinkClear={onLinkClear}
      onDelete={onDelete}
      linkHrefRequired={linkHrefRequired}
      error={error}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 10 : undefined,
      }}
      handle={
        <Box
          {...attributes}
          {...listeners}
          className="vtwo:flex vtwo:cursor-grab vtwo:shrink-0 vtwo:touch-none"
        >
          <DragIndicatorIcon className="vtwo:text-gray-400 vtwo:text-[20px]" />
        </Box>
      }
    />
  );
}

export type ImageLinkManagerWidgetProps = WidgetProps & {
  itemErrors?: Record<number, string | undefined>;
};

export function ImageLinkManagerWidget(props: ImageLinkManagerWidgetProps): ReactElement {
  const { value, onChange, uiSchema, schema, itemErrors = {} } = props;

  const items = (value as ImageLinkItem[] | undefined) ?? [];
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [assetPickerOpen, setAssetPickerOpen] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [cropImageSrc, setCropImageSrc] = useState<string | null>(null);
  const [cropFileName, setCropFileName] = useState<string>('');
  const [pendingAlt, setPendingAlt] = useState<string>('');
  const [cropKey, setCropKey] = useState(0);
  const [searchParams] = useSearchParams();
  const organisationId = searchParams.get('organisationId') ?? '';
  const { mutate: uploadAsset, isPending } = useUploadAsset();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const {
    imageSources,
    sources,
    sortable = false,
    addButtonLabel = 'Add image',
    aspectRatio,
  } = (uiSchema?.['ui:options'] ?? {}) as ImageLinkManagerOptions;

  const resolvedSources = imageSources ?? sources ?? [];

  const linkHrefRequired = getLinkHrefRequired(schema);
  const canReorder = sortable && items.length > 1;

  function handleSourceClick(source: string) {
    setAnchorEl(null);
    if (source === 'disk') {
      setUploadModalOpen(true);
    } else if (source === 'assets') {
      setAssetPickerOpen(true);
    }
  }

  function handleFileSelected(file: File) {
    setUploadModalOpen(false);
    setPendingAlt(file.name);
    setCropFileName(buildDefaultFileName(file.name, aspectRatio));
    setCropKey((k) => k + 1);
    const reader = new FileReader();
    reader.onload = () => {
      setCropImageSrc(reader.result as string);
    };
    reader.readAsDataURL(file);
  }

  function handleCropDone(file: File) {
    setCropImageSrc(null);
    uploadAsset(
      { file, organisationId },
      {
        onSuccess: ({ id, thumbnailUrl, url }) => {
          onChange([...items, { assetId: id, thumbnailUrl, imageUrl: url, alt: pendingAlt }]);
        },
      }
    );
  }

  function handleAssetSelect(asset: Asset) {
    onChange([
      ...items,
      {
        assetId: asset.id,
        thumbnailUrl: asset.thumbnailUrl,
        imageUrl: asset.url,
        alt: asset.originalFilename,
      },
    ]);
    setAssetPickerOpen(false);
  }

  function handleDelete(index: number) {
    onChange(items.filter((_, i) => i !== index));
  }

  function handleLinkChange(index: number, fields: LinkFields) {
    onChange(items.map((it, i) => (i === index ? { ...it, ...fields } : it)));
  }

  function handleLinkClear(index: number) {
    onChange(
      items.map((it, i): ImageLinkItem => {
        if (i !== index) return it;
        return {
          assetId: it.assetId,
          thumbnailUrl: it.thumbnailUrl,
          imageUrl: it.imageUrl,
          ...(it.alt !== undefined ? { alt: it.alt } : {}),
        };
      })
    );
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((item) => item.assetId === String(active.id));
    const newIndex = items.findIndex((item) => item.assetId === String(over.id));
    if (oldIndex === -1 || newIndex === -1) return;
    onChange(arrayMove(items, oldIndex, newIndex));
  }

  let rows: ReactNode;
  if (canReorder) {
    rows = (
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
        modifiers={[restrictToVerticalAxis]}
      >
        <SortableContext
          items={items.map((item) => item.assetId)}
          strategy={verticalListSortingStrategy}
        >
          {items.map((item, index) => (
            <SortableImageLinkRow
              key={item.assetId}
              id={item.assetId}
              item={item}
              onLinkChange={(fields) => handleLinkChange(index, fields)}
              onLinkClear={() => handleLinkClear(index)}
              onDelete={() => handleDelete(index)}
              linkHrefRequired={linkHrefRequired}
              error={itemErrors[index]}
            />
          ))}
        </SortableContext>
      </DndContext>
    );
  } else {
    rows = items.map((item, index) => (
      <ImageLinkRow
        key={item.assetId}
        item={item}
        onLinkChange={(fields) => handleLinkChange(index, fields)}
        onLinkClear={() => handleLinkClear(index)}
        onDelete={() => handleDelete(index)}
        linkHrefRequired={linkHrefRequired}
        error={itemErrors[index]}
      />
    ));
  }

  return (
    <Box className="vtwo:flex vtwo:flex-col vtwo:gap-2">
      {rows}

      <Button
        variant="outlined"
        startIcon={isPending ? <CircularProgress size={16} /> : <AddIcon />}
        fullWidth
        disabled={isPending}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        className="vtwo:normal-case vtwo:text-gray-600 vtwo:border-plus-border vtwo:py-2 vtwo:rounded-lg"
      >
        {addButtonLabel}
      </Button>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        {resolvedSources.map((source) => (
          <MenuItem key={source} onClick={() => handleSourceClick(source)}>
            {SOURCE_LABELS[source] ?? source}
          </MenuItem>
        ))}
      </Menu>

      <FileUploadModal
        open={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        onFileSelected={handleFileSelected}
      />

      <ImageCropModal
        key={cropKey}
        open={Boolean(cropImageSrc)}
        imageSrc={cropImageSrc ?? ''}
        aspectRatio={aspectRatio}
        defaultFileName={cropFileName}
        onClose={() => setCropImageSrc(null)}
        onCropDone={handleCropDone}
      />

      <AssetPickerModal
        open={assetPickerOpen}
        onClose={() => setAssetPickerOpen(false)}
        onSelect={handleAssetSelect}
        organisationId={organisationId}
        aspectRatio={aspectRatio}
      />
    </Box>
  );
}
